import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { getAllUsers, createUser, updateUser, deleteUser, getUserById, getAllCellules, getAllBureaux } from '../services/api';

const Utilisateurs = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    nom: '',
    prenom: '',
    telephone: '',
    role: 'user',
    cellule: '',
    bureau: '',
  });
  const [cellules, setCellules] = useState([]);
  const [bureaux, setBureaux] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  // Options pour le sélecteur de rôle
  const roleOptions = [
    { value: 'user', label: 'Utilisateur' },
    { value: 'admin', label: 'Administrateur' },
    { value: 'almer', label: 'ALMER' },
    { value: 'ministre', label: 'Ministre' },
  ];

  useEffect(() => {
    loadUsers();
    loadCellules();
    loadBureaux();
  }, []);

  const loadCellules = async () => {
    try {
      const data = await getAllCellules();
      console.log('Cellules chargées:', data);
      setCellules(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erreur lors du chargement des cellules:', error);
      setCellules([]);
    }
  };

  const loadBureaux = async () => {
    try {
      const data = await getAllBureaux();
      console.log('Bureaux chargés:', data);
      setBureaux(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erreur lors du chargement des bureaux:', error);
      setBureaux([]);
    }
  };

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await getAllUsers();
      setUsers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erreur lors du chargement des utilisateurs:', error);
      setMessage({ type: 'error', text: 'Erreur lors du chargement des utilisateurs' });
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleRoleChange = (selectedOption) => {
    setFormData((prev) => ({ ...prev, role: selectedOption.value }));
  };

  const handleCelluleChange = (selectedOption) => {
    setFormData((prev) => ({ ...prev, cellule: selectedOption ? selectedOption.value : '' }));
  };

  const handleBureauChange = (selectedOption) => {
    setFormData((prev) => ({ ...prev, bureau: selectedOption ? selectedOption.value : '' }));
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (!formData.password || formData.password.length < 6) {
      setMessage({ type: 'error', text: 'Le mot de passe doit contenir au moins 6 caractères' });
      return;
    }

    setLoading(true);
    try {
      await createUser(formData);
      setMessage({ type: 'success', text: 'Utilisateur créé avec succès' });
      setShowCreateForm(false);
      resetForm();
      loadUsers();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Erreur lors de la création de l\'utilisateur',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    setLoading(true);
    try {
      const updateData = { ...formData };
      // Ne pas envoyer le mot de passe s'il est vide
      if (!updateData.password) {
        delete updateData.password;
      }
      await updateUser(editingUser.id, updateData);
      setMessage({ type: 'success', text: 'Utilisateur mis à jour avec succès' });
      setEditingUser(null);
      resetForm();
      loadUsers();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Erreur lors de la mise à jour de l\'utilisateur',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer l'utilisateur "${username}" ?\nCette action est irréversible.`)) {
      return;
    }

    try {
      await deleteUser(userId);
      setMessage({ type: 'success', text: 'Utilisateur supprimé avec succès' });
      loadUsers();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Erreur lors de la suppression de l\'utilisateur',
      });
    }
  };

  const resetForm = () => {
    setFormData({
      username: '',
      email: '',
      password: '',
      nom: '',
      prenom: '',
      telephone: '',
      role: 'user',
      cellule: '',
      bureau: '',
    });
  };

  const openEditForm = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username || '',
      email: user.email || '',
      password: '',
      nom: user.nom || '',
      prenom: user.prenom || '',
      telephone: user.telephone || '',
      role: user.role || 'user',
      cellule: user.cellule || '',
      bureau: user.bureau || '',
    });
    setShowCreateForm(true);
  };

  const closeForm = () => {
    setShowCreateForm(false);
    setEditingUser(null);
    resetForm();
    setMessage({ type: '', text: '' });
  };

  // Calculs de pagination
  const totalPages = Math.ceil((Array.isArray(users) ? users.length : 0) / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentUsers = Array.isArray(users) ? users.slice(startIndex, endIndex) : [];

  // Gestion de la pagination
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      // Scroll vers le haut de la table
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Réinitialiser à la page 1 quand les utilisateurs changent
  useEffect(() => {
    setCurrentPage(1);
  }, [users.length]);

  // Fonction pour afficher les détails d'un utilisateur
  const handleViewDetails = async (userId) => {
    setLoadingDetails(true);
    setShowDetails(true);
    try {
      const userDetails = await getUserById(userId);
      setSelectedUser(userDetails);
    } catch (error) {
      console.error('Erreur lors du chargement des détails:', error);
      setMessage({
        type: 'error',
        text: 'Erreur lors du chargement des détails de l\'utilisateur',
      });
      setShowDetails(false);
    } finally {
      setLoadingDetails(false);
    }
  };

  const closeDetails = () => {
    setShowDetails(false);
    setSelectedUser(null);
  };

  return (
    <div className="p-8 bg-background min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-text flex items-center">
            <i className="fas fa-users mr-3 text-primary"></i>
            Gestion des Utilisateurs
          </h1>
          <button
            onClick={() => {
              closeForm();
              setShowCreateForm(true);
            }}
            className="px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-all"
          >
            <i className="fas fa-user-plus mr-2"></i>Nouvel Utilisateur
          </button>
        </div>

        {/* Message */}
        {message.text && (
          <div
            className={`mb-4 p-4 rounded-lg ${
              message.type === 'success'
                ? 'bg-green-100 text-green-700 border border-green-400'
                : 'bg-red-100 text-red-700 border border-red-400'
            }`}
          >
            <i
              className={`fas ${
                message.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'
              } mr-2`}
            ></i>
            {message.text}
          </div>
        )}

        {/* Formulaire de création/édition */}
        {showCreateForm && (
          <div className="bg-cardBg rounded-lg shadow-lg p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-text">
                {editingUser ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}
              </h2>
              <button
                onClick={closeForm}
                className="text-gray-500 hover:text-gray-700"
              >
                <i className="fas fa-times text-xl"></i>
              </button>
            </div>

            <form onSubmit={editingUser ? handleUpdateUser : handleCreateUser}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text mb-2">
                    Nom d'utilisateur *
                  </label>
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text mb-2">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text mb-2">
                    {editingUser ? 'Nouveau mot de passe (laisser vide pour ne pas changer)' : 'Mot de passe *'}
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required={!editingUser}
                    minLength={6}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text mb-2">Rôle *</label>
                  <Select
                    value={roleOptions.find((option) => option.value === formData.role)}
                    onChange={handleRoleChange}
                    options={roleOptions}
                    isSearchable={false}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    styles={{
                      control: (base) => ({
                        ...base,
                        borderColor: '#d1d5db',
                        boxShadow: 'none',
                        '&:hover': {
                          borderColor: '#00A896',
                        },
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected
                          ? '#00A896'
                          : state.isFocused
                          ? '#E0F7F5'
                          : 'white',
                        color: state.isSelected ? 'white' : '#333',
                        '&:hover': {
                          backgroundColor: state.isSelected ? '#00A896' : '#E0F7F5',
                        },
                      }),
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text mb-2">Nom</label>
                  <input
                    type="text"
                    name="nom"
                    value={formData.nom}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text mb-2">Prénom</label>
                  <input
                    type="text"
                    name="prenom"
                    value={formData.prenom}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text mb-2">Téléphone</label>
                  <input
                    type="tel"
                    name="telephone"
                    value={formData.telephone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text mb-2">Cellule</label>
                  <Select
                    value={
                      formData.cellule && cellules.length > 0
                        ? cellules
                            .filter((c) => {
                              const cellValue = c.id || c.nom;
                              return cellValue == formData.cellule || c.nom === formData.cellule;
                            })
                            .map((c) => ({
                              value: c.id || c.nom,
                              label: c.nom || String(c.id || c.nom),
                            }))[0] || null
                        : null
                    }
                    onChange={handleCelluleChange}
                    options={cellules.map((c) => ({
                      value: c.id || c.nom,
                      label: c.nom || String(c.id || c.nom),
                    }))}
                    isClearable
                    isSearchable
                    placeholder={cellules.length === 0 ? "Chargement des cellules..." : "Sélectionner une cellule"}
                    isLoading={cellules.length === 0}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    styles={{
                      control: (base) => ({
                        ...base,
                        borderColor: '#d1d5db',
                        boxShadow: 'none',
                        '&:hover': {
                          borderColor: '#00A896',
                        },
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected
                          ? '#00A896'
                          : state.isFocused
                          ? '#E0F7F5'
                          : 'white',
                        color: state.isSelected ? 'white' : '#333',
                        '&:hover': {
                          backgroundColor: state.isSelected ? '#00A896' : '#E0F7F5',
                        },
                      }),
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text mb-2">Bureau</label>
                  <Select
                    value={
                      formData.bureau && bureaux.length > 0
                        ? bureaux
                            .filter((b) => {
                              const bureauValue = b.id || b.nom;
                              return bureauValue == formData.bureau || b.nom === formData.bureau;
                            })
                            .map((b) => ({
                              value: b.id || b.nom,
                              label: b.nom || String(b.id || b.nom),
                            }))[0] || null
                        : null
                    }
                    onChange={handleBureauChange}
                    options={bureaux.map((b) => ({
                      value: b.id || b.nom,
                      label: b.nom || String(b.id || b.nom),
                    }))}
                    isClearable
                    isSearchable
                    placeholder={bureaux.length === 0 ? "Chargement des bureaux..." : "Sélectionner un bureau"}
                    isLoading={bureaux.length === 0}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    styles={{
                      control: (base) => ({
                        ...base,
                        borderColor: '#d1d5db',
                        boxShadow: 'none',
                        '&:hover': {
                          borderColor: '#00A896',
                        },
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected
                          ? '#00A896'
                          : state.isFocused
                          ? '#E0F7F5'
                          : 'white',
                        color: state.isSelected ? 'white' : '#333',
                        '&:hover': {
                          backgroundColor: state.isSelected ? '#00A896' : '#E0F7F5',
                        },
                      }),
                    }}
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-4">
                <button
                  type="button"
                  onClick={closeForm}
                  className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-all"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>Enregistrement...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save mr-2"></i>Enregistrer
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Table des utilisateurs */}
        <div className="bg-cardBg rounded-lg shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-primary text-white">
                <tr>
                  <th className="px-6 py-4 text-left">Nom d'utilisateur</th>
                  <th className="px-6 py-4 text-left">Nom</th>
                  <th className="px-6 py-4 text-left">Prénom</th>
                  <th className="px-6 py-4 text-left">Email</th>
                  <th className="px-6 py-4 text-left">Téléphone</th>
                  <th className="px-6 py-4 text-left">Rôle</th>
                  <th className="px-6 py-4 text-left">Statut</th>
                  <th className="px-6 py-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-8 text-center">
                      <i className="fas fa-spinner fa-spin text-2xl text-primary"></i>
                    </td>
                  </tr>
                ) : !Array.isArray(users) || users.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-8 text-center text-text-light">
                      Aucun utilisateur trouvé
                    </td>
                  </tr>
                ) : (
                  currentUsers.map((user) => (
                    <tr key={user.id} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">@{user.username}</td>
                      <td className="px-6 py-4">{user.nom || '-'}</td>
                      <td className="px-6 py-4">{user.prenom || '-'}</td>
                      <td className="px-6 py-4">{user.email}</td>
                      <td className="px-6 py-4">{user.telephone || '-'}</td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            user.role === 'admin'
                              ? 'bg-purple-100 text-purple-800'
                              : user.role === 'almer'
                              ? 'bg-orange-100 text-orange-800'
                              : user.role === 'ministre'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}
                        >
                          {user.role === 'admin'
                            ? 'Administrateur'
                            : user.role === 'almer'
                            ? 'ALMER'
                            : user.role === 'ministre'
                            ? 'Ministre'
                            : 'Utilisateur'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            user.statut === 'actif'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {user.statut === 'actif' ? 'Actif' : 'Inactif'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleViewDetails(user.id)}
                            className="px-3 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-all text-sm"
                            title="Voir les détails"
                          >
                            <i className="fas fa-eye"></i>
                          </button>
                          <button
                            onClick={() => openEditForm(user)}
                            className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all text-sm"
                            title="Modifier"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user.id, user.username)}
                            className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all text-sm"
                            title="Supprimer"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {Array.isArray(users) && users.length > 0 && totalPages > 1 && (
          <div className="mt-6 bg-cardBg rounded-lg shadow-lg p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-text-light">
                Affichage de <strong>{startIndex + 1}</strong> à <strong>{Math.min(endIndex, users.length)}</strong> sur <strong>{users.length}</strong> utilisateur(s)
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <i className="fas fa-chevron-left"></i>
                </button>
                
                {/* Numéros de page */}
                <div className="flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                    // Afficher seulement certaines pages pour éviter trop de boutons
                    if (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`px-4 py-2 rounded-lg transition-all ${
                            currentPage === page
                              ? 'bg-primary text-white'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    } else if (page === currentPage - 2 || page === currentPage + 2) {
                      return (
                        <span key={page} className="px-2 text-gray-500">
                          ...
                        </span>
                      );
                    }
                    return null;
                  })}
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <i className="fas fa-chevron-right"></i>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Stats */}
        {Array.isArray(users) && users.length > 0 && (
          <div className="mt-4 bg-cardBg rounded-lg shadow-lg p-4">
            <p className="text-text-light text-sm">
              <i className="fas fa-info-circle mr-2 text-primary"></i>
              Total: <strong>{users.length}</strong> utilisateur(s)
              {totalPages > 1 && (
                <span className="ml-4">
                  | Page <strong>{currentPage}</strong> sur <strong>{totalPages}</strong>
                </span>
              )}
            </p>
          </div>
        )}

        {/* Modale de détails */}
        {showDetails && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-cardBg rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-cardBg border-b border-gray-200 p-6 flex justify-between items-center">
                <h2 className="text-2xl font-bold text-text flex items-center">
                  <i className="fas fa-user-circle mr-3 text-primary"></i>
                  Détails de l'utilisateur
                </h2>
                <button
                  onClick={closeDetails}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <div className="p-6">
                  {loadingDetails ? (
                    <div className="flex items-center justify-center py-12">
                      <i className="fas fa-spinner fa-spin text-4xl text-primary"></i>
                    </div>
                  ) : selectedUser ? (
                    <div className="space-y-6">
                      {/* Avatar et nom */}
                      <div className="flex items-center mb-6 pb-6 border-b border-gray-200">
                        <div className="w-20 h-20 bg-primary rounded-full flex items-center justify-center text-white text-3xl font-bold mr-6">
                          {selectedUser.nom?.[0]?.toUpperCase() || selectedUser.username?.[0]?.toUpperCase() || 'U'}
                        </div>
                        <div>
                          <h3 className="text-2xl font-bold text-text">
                            {selectedUser.nom} {selectedUser.prenom}
                          </h3>
                          <p className="text-text-light">@{selectedUser.username}</p>
                        </div>
                      </div>

                      {/* Informations détaillées */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-text-light mb-1">
                            ID
                          </label>
                          <p className="text-text font-medium">{selectedUser.id}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-text-light mb-1">
                            Nom d'utilisateur
                          </label>
                          <p className="text-text font-medium">@{selectedUser.username}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-text-light mb-1">
                            Email
                          </label>
                          <p className="text-text font-medium">{selectedUser.email}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-text-light mb-1">
                            Téléphone
                          </label>
                          <p className="text-text font-medium">{selectedUser.telephone || '-'}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-text-light mb-1">
                            Nom
                          </label>
                          <p className="text-text font-medium">{selectedUser.nom || '-'}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-text-light mb-1">
                            Prénom
                          </label>
                          <p className="text-text font-medium">{selectedUser.prenom || '-'}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-text-light mb-1">
                            Rôle
                          </label>
                          <span
                            className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                              selectedUser.role === 'admin'
                                ? 'bg-purple-100 text-purple-800'
                                : selectedUser.role === 'almer'
                                ? 'bg-orange-100 text-orange-800'
                                : selectedUser.role === 'ministre'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-blue-100 text-blue-800'
                            }`}
                          >
                            {selectedUser.role === 'admin'
                              ? 'Administrateur'
                              : selectedUser.role === 'almer'
                              ? 'ALMER'
                              : selectedUser.role === 'ministre'
                              ? 'Ministre'
                              : 'Utilisateur'}
                          </span>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-text-light mb-1">
                            Statut
                          </label>
                          <span
                            className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                              selectedUser.statut === 'actif'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {selectedUser.statut === 'actif' ? 'Actif' : 'Inactif'}
                          </span>
                        </div>
                        {selectedUser.date_creation && (
                          <div>
                            <label className="block text-sm font-medium text-text-light mb-1">
                              Date de création
                            </label>
                            <p className="text-text font-medium">
                              {new Date(selectedUser.date_creation).toLocaleDateString('fr-FR', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                              })}
                            </p>
                          </div>
                        )}
                        {selectedUser.date_modification && (
                          <div>
                            <label className="block text-sm font-medium text-text-light mb-1">
                              Dernière modification
                            </label>
                            <p className="text-text font-medium">
                              {new Date(selectedUser.date_modification).toLocaleDateString('fr-FR', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                              })}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <p className="text-text-light">Impossible de charger les détails</p>
                    </div>
                  )}
                </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Utilisateurs;

